import { Injectable, Inject } from '@angular/core';
import { OKTA_AUTH } from '@okta/okta-angular';
import OktaAuth from '@okta/okta-auth-js';
import { BehaviorSubject, Observable, from } from 'rxjs';
import { tap, shareReplay } from 'rxjs/operators';
import { AppService } from '../app.service';

@Injectable({
  providedIn: 'root'
})
export class OktaUserInfoService {
  private userDataSubject = new BehaviorSubject<any>(null);
  public userData$ = this.userDataSubject.asObservable();
  
  // Cache the API call with shareReplay
  private userRequest: Observable<any>;

  constructor(
    @Inject(OKTA_AUTH) private oktaAuth: OktaAuth,
    private appService: AppService
  ) {}

  /**
   * Gets Okta user information and caches it
   * Updates app config with username and iNumber
   */
  getUserInfo(): Observable<any> {
    // If we don't have a request in progress, create one
    if (!this.userRequest) {
      this.userRequest = from(this.oktaAuth.getUser()).pipe(
        tap(user => {
          // Update the config when we get user data
          const config = this.appService.readConfig() || ({} as any);
          config.username = user.preferred_username;
          config.userINumber = config.username.split('@')[0];

          // Determine if user is federated (defensively ensure function exists)
            const getAccessTokenFn: any = (this.oktaAuth as any)?.getAccessToken;
            if (typeof getAccessTokenFn === 'function') {
              try {
                const token = getAccessTokenFn.call(this.oktaAuth);
                if (token) {
                  const payloadPart = token.split('.')[1];
                  if (payloadPart) {
                    const payload = JSON.parse(atob(payloadPart));
                    config.isFederated = payload.isFederated;
                  }
                }
              } catch (e) {
                // Swallow parsing errors in test environments; log for visibility otherwise
                // eslint-disable-next-line no-console
                console.error('Error parsing token:', e);
              }
            }

          // Emit the new user data
          this.userDataSubject.next(user);
        }),
        // Share the same result with all subscribers
        shareReplay(1)
      );
    }

    return this.userRequest;
  }
  
  /**
   * Forces a refresh of the user data
   */
  refreshUserInfo(): Observable<any> {
    this.userRequest = null;
    return this.getUserInfo();
  }
}